import { Type } from '@sinclair/typebox';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { TfUserSchema } from './tables.js';
// Phase 5 — admin surface shared schemas.
//
// These describe the privileged endpoints used by hub operators:
// /api-keys (CRUD), /users (read + lifecycle), and /auth/bootstrap-admin.
// Every body and response shape is shared so the hub Fastify routes and the
// hub-client typed registry agree on the wire format.
// --- /api-keys ---
/** Public projection of `tf_api_key` — never includes `keyHash`. */
export const ApiKeySchema = Type.Object({
    id: UuidSchema,
    name: Type.String(),
    prefix: Type.String(),
    userId: Type.Union([UuidSchema, Type.Null()]),
    /** Track this key is bound to one-to-one (HUB-089); null = hub-wide key. */
    trackId: Type.Union([UuidSchema, Type.Null()]),
    createdAt: IsoDateTimeSchema,
    revokedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
});
export const ApiKeyListResponseSchema = Type.Object({
    apiKeys: Type.Array(ApiKeySchema),
});
// POST /api-keys
export const CreateApiKeyBodySchema = Type.Object({
    name: Type.String({ minLength: 1, maxLength: 200 }),
    /** Bind the key to a hub user. Absent → machine principal (system-admin). */
    userId: Type.Optional(UuidSchema),
});
/**
 * The mint endpoint returns plaintext exactly once — clients must persist it
 * server-side before showing it to the operator. Subsequent reads only ever
 * return the prefix.
 */
export const CreateApiKeyResponseSchema = Type.Composite([
    ApiKeySchema,
    Type.Object({
        plaintext: Type.String(),
    }),
]);
export const ApiKeyIdParamsSchema = Type.Object({
    id: UuidSchema,
});
// POST /tracks/:id/api-key/assign — bind an existing unbound machine key to a
// track (HUB-089). Repair path for tracks whose row was first created by a
// human/seed principal, so the deployed pod's key never got bound.
export const AssignApiKeyBodySchema = Type.Object({
    keyId: UuidSchema,
});
// --- /users ---
export const UserListResponseSchema = Type.Object({
    users: Type.Array(TfUserSchema),
});
export const UserResponseSchema = Type.Object({
    user: TfUserSchema,
});
export const UserIdParamsSchema = Type.Object({
    id: UuidSchema,
});
// POST /users — admin-provisioned user creation (SSO-first).
// Password is optional; omit it for Google-SSO-only accounts. First verified
// SSO login links the pre-seeded row by email and reuses the pre-seeded id.
export const CreateUserBodySchema = Type.Object({
    email: Type.String({ format: 'email', maxLength: 320 }),
    name: Type.Optional(Type.String({ maxLength: 200 })),
    roles: Type.Optional(Type.Array(Type.String({ minLength: 1, maxLength: 64 }), { maxItems: 16 })),
    password: Type.Optional(Type.String({ minLength: 8, maxLength: 256 })), // omit for SSO-only
});
// PATCH /users/:id — partial admin edit. Email is intentionally omitted; if a
// user changes email it must go through the verify-email flow.
export const UpdateUserBodySchema = Type.Object({
    name: Type.Optional(Type.Union([Type.String({ maxLength: 200 }), Type.Null()])),
    avatarUrl: Type.Optional(Type.Union([Type.String({ maxLength: 1024 }), Type.Null()])),
    roles: Type.Optional(Type.Array(Type.String({ minLength: 1, maxLength: 64 }), { maxItems: 16 })),
});
// --- /auth/bootstrap-admin ---
/**
 * One-shot endpoint to mint the very first system admin so operators don't
 * face a chicken-and-egg problem. The hub rejects calls when `INITIAL_ADMIN_TOKEN`
 * isn't configured (404), the supplied token doesn't match (401), or any
 * admin user already exists (409). On success, returns a TokenPair so the
 * caller is logged in immediately.
 */
export const BootstrapAdminBodySchema = Type.Object({
    email: Type.String({ format: 'email', maxLength: 320 }),
    password: Type.String({ minLength: 8, maxLength: 256 }),
    name: Type.Optional(Type.String({ maxLength: 200 })),
    /** Must equal the `INITIAL_ADMIN_TOKEN` env var on the hub. */
    token: Type.String({ minLength: 8, maxLength: 512 }),
});
/**
 * Status probe for the admin SPA: is the bootstrap-admin endpoint enabled
 * (i.e. INITIAL_ADMIN_TOKEN configured), and does any admin user already
 * exist? Together these answer "should the SPA render the bootstrap form?".
 *
 * The endpoint is unauthenticated and exposes only two booleans, neither of
 * which is sensitive on its own.
 */
export const BootstrapStatusResponseSchema = Type.Object({
    enabled: Type.Boolean(),
    hasAdmin: Type.Boolean(),
});
/**
 * Read-only snapshot of the hub's SSO surface, served unauthenticated so the
 * portal SPA can render the right buttons and self-redirects without needing
 * a Bearer token (the user hasn't signed in yet). Adding fields here is
 * back-compat; never remove them — old SPAs read tomorrow's hub.
 */
export const SsoInfoResponseSchema = Type.Object({
    /** Which OAuth providers the operator has configured. Buttons render 1:1. */
    oauthProviders: Type.Array(Type.String()),
    /**
     * Whether a "Forgot password?" flow can succeed: the password-reset routes
     * are mounted AND `email.provider` sends mail (anything but `noop`, the
     * same rule as the admin invite — HUB-106 D4). A login page hides the
     * link when false.
     */
    passwordResetEnabled: Type.Boolean(),
    /**
     * The admin-set `portal.landing_url` (HUB-125), or null when unset. A login
     * page sends a sign-in with no `return_to` here instead of the hub root.
     */
    landingUrl: Type.Union([Type.String(), Type.Null()]),
});
/**
 * Validates a candidate SSO `return_to` URL against the hub's allowlist.
 * External portal SPAs must ask the hub before handing tokens to a target
 * origin; otherwise a direct portal URL could bypass the hub's `/login`
 * validation and exfiltrate token fragments.
 */
export const SsoReturnToQuerySchema = Type.Object({
    return_to: Type.Optional(Type.String()),
});
export const SsoReturnToResponseSchema = Type.Object({
    allowed: Type.Boolean(),
    returnTo: Type.Union([Type.String(), Type.Null()]),
});
//# sourceMappingURL=admin.js.map